import { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Header Component
const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? 'bg-discord-darker-bg/95 backdrop-blur-sm shadow-lg py-2' 
          : 'bg-transparent py-4'
      }`}
    >
      <nav className="container mx-auto flex justify-between items-center px-4">
        <Link 
          to="/" 
          className="text-2xl font-bold flex items-center gap-2 group"
        >
          <div className="w-10 h-10 bg-discord-blurple rounded-xl flex items-center justify-center transition-all duration-300 group-hover:rotate-6">
            <span className="text-white font-bold text-xl">O</span>
          </div>
          <div>
            <span className="text-discord-blurple hover:text-discord-secondary transition-colors duration-300">Obituaries.</span>
            <span className="text-glow-purple font-light">io</span>
          </div>
        </Link>
        
        <div className="flex gap-4 items-center">
          <Link 
            to="/create" 
            className="discord-btn px-4 py-2 rounded-md flex items-center gap-2 text-white"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Create Memorial
          </Link>
        </div>
      </nav>
    </header>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-discord-darker-bg border-t border-discord-highlight/20">
      <div className="container mx-auto py-10 px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-discord-blurple rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-xl">O</span>
              </div>
              <div className="text-2xl font-bold">
                <span className="text-discord-blurple">Obituaries.</span>
                <span className="text-glow-purple font-light">io</span>
              </div>
            </div>
            <p className="text-discord-text mb-4">
              Immortalizing digital identities and preserving online legacies forever.
            </p>
            <div className="flex gap-4 text-discord-muted">
              <a href="#" className="hover:text-discord-blurple transition-colors duration-200">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z"/>
                </svg>
              </a>
              <a href="#" className="hover:text-discord-blurple transition-colors duration-200">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                </svg>
              </a>
              <a href="#" className="hover:text-discord-blurple transition-colors duration-200">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                </svg>
              </a>
            </div>
          </div>
          
          <div>
            <h3 className="text-white font-medium text-lg mb-4">Resources</h3>
            <ul className="space-y-2 text-discord-muted">
              <li><a href="#" className="hover:text-discord-blurple transition-colors duration-200">Help Center</a></li>
              <li><a href="#" className="hover:text-discord-blurple transition-colors duration-200">About Us</a></li>
              <li><a href="#" className="hover:text-discord-blurple transition-colors duration-200">Contact</a></li>
              <li><a href="#" className="hover:text-discord-blurple transition-colors duration-200">Blog</a></li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-white font-medium text-lg mb-4">Legal</h3>
            <ul className="space-y-2 text-discord-muted">
              <li><a href="#" className="hover:text-discord-blurple transition-colors duration-200">Privacy Policy</a></li>
              <li><a href="#" className="hover:text-discord-blurple transition-colors duration-200">Terms of Service</a></li>
              <li><a href="#" className="hover:text-discord-blurple transition-colors duration-200">Cookie Policy</a></li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-discord-highlight/20 mt-8 pt-8 text-center text-discord-muted">
          <p className="mb-2">© 2025 Obituaries.io - All rights reserved</p>
          <p className="text-sm pulse-animation">
            Memories live forever online. Every tribute and memorial is permanently preserved in digital eternity.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Home Page
const Home = () => {
  const [memorials, setMemorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFeature, setActiveFeature] = useState(0);
  const [activeStory, setActiveStory] = useState(0);
  const [showStatCounter, setShowStatCounter] = useState(false);
  
  const features = [
    {
      title: "Create Eternal Memorials",
      description: "Build a permanent shrine for gamertags, usernames, and online identities that will last forever.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
        </svg>
      ),
    },
    {
      title: "Honor Digital Relationships",
      description: "Celebrate connections formed in games, social platforms, and online communities that are just as real and meaningful as physical ones.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" viewBox="0 0 20 20" fill="currentColor">
          <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
        </svg>
      ),
    },
    {
      title: "Preserve Digital Legacies",
      description: "Ensure that someone's impact on online communities won't be forgotten with the passing of time or changing of platforms.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zM7 8H5v2h2V8zm2 0h2v2H9V8zm6 0h-2v2h2V8z" clipRule="evenodd" />
        </svg>
      ),
    },
  ];
  
  const stories = [
    {
      handle: "RaidLeader_Steve",
      platform: "World of Warcraft",
      quote: "We spent 6 years raiding together, 3 nights a week. I never knew his real name or face, but when we heard he passed away, our guild was devastated. His memorial page has been our way to stay connected to him.",
      author: "GuildMate472"
    },
    {
      handle: "PixelArtist",
      platform: "Twitter & DeviantArt",
      quote: "She shared her art with thousands online for years. After she died, all those accounts would have eventually been deleted. Now her creative legacy lives on where her followers can still find it.",
      author: "ArtCollector88"
    },
    {
      handle: "SniperElite",
      platform: "Discord & Xbox",
      quote: "When my best online friend suddenly stopped logging in, I had no way to know what happened. Months later, his brother found us and let us know. If this platform existed then, we wouldn't have spent months in painful uncertainty.",
      author: "TeamPlayer22"
    }
  ];

  // Change feature every 5 seconds
  useEffect(() => {
    const intervalFeature = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 5000);
    
    const intervalStory = setInterval(() => {
      setActiveStory((prev) => (prev + 1) % stories.length);
    }, 8000);
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setShowStatCounter(true);
        }
      });
    }, { threshold: 0.5 });
    
    const statsSection = document.getElementById('stats-section');
    if (statsSection) {
      observer.observe(statsSection);
    }
    
    return () => {
      clearInterval(intervalFeature);
      clearInterval(intervalStory);
      if (statsSection) {
        observer.unobserve(statsSection);
      }
    };
  }, [features.length, stories.length]);

  useEffect(() => {
    const fetchMemorials = async () => {
      try {
        const response = await axios.get(`${API}/memorials`);
        setMemorials(Array.isArray(response.data) ? response.data : (response.data ? [response.data] : []));
        setLoading(false);
      } catch (err) {
        console.error("Error fetching memorials:", err);
        setLoading(false);
      }
    };

    fetchMemorials();
  }, []);
  
  // Counter animation component
  const StatCounter = ({ end, duration = 2000, label, icon }) => {
    const [count, setCount] = useState(0);
    
    useEffect(() => {
      if (!showStatCounter) return;
      
      let startTimestamp = null;
      const step = (timestamp) => {
        if (!startTimestamp) startTimestamp = timestamp;
        const progress = Math.min((timestamp - startTimestamp) / duration, 1);
        setCount(Math.floor(progress * end));
        
        if (progress < 1) {
          window.requestAnimationFrame(step);
        }
      };
      
      window.requestAnimationFrame(step);
    }, [end, duration, showStatCounter]);
    
    return (
      <div className="text-center">
        <div className="w-16 h-16 rounded-full bg-discord-blurple/20 mx-auto mb-3 flex items-center justify-center">
          {icon}
        </div>
        <div className="text-4xl font-bold text-white mb-2">{count}%</div>
        <div className="text-discord-text">{label}</div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-discord-darker-bg text-discord-text">
      {/* Hero Section */}
      <div className="relative pt-20">
        {/* Animated background with particle effect */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <div className="absolute inset-0 animated-gradient opacity-30"></div>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIHZpZXdCb3g9IjAgMCAxNiAxNiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48Y2lyY2xlIGN4PSI4IiBjeT0iOCIgcj0iMyIgZmlsbD0id2hpdGUiIGZpbGwtb3BhY2l0eT0iMC4xIi8+PC9zdmc+')] opacity-10"></div>
        </div>
        
        <div className="relative z-10 pt-20 pb-32 px-4">
          <div className="container mx-auto">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
              <div className="w-full lg:w-1/2 text-center lg:text-left">
                <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
                  <span className="block text-gradient font-extrabold">Immortalize</span> 
                  <span className="block text-white">Your Digital Legacy</span>
                </h1>
                
                <p className="text-xl text-discord-text mb-8 max-w-2xl">
                  For the friends you've never met in person but who changed your life. For the communities where you truly belonged. For the connections that transcend physical spaces.
                </p>
                
                <div className="h-52 relative mb-8">
                  {features.map((feature, index) => (
                    <div 
                      key={index}
                      className={`absolute inset-0 transition-all duration-700 ease-in-out flex flex-col ${
                        index === activeFeature 
                          ? "opacity-100 transform-none" 
                          : "opacity-0 translate-y-8"
                      }`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-discord-blurple flex items-center justify-center">
                          {feature.icon}
                        </div>
                        <h2 className="text-2xl font-bold text-white">{feature.title}</h2>
                      </div>
                      <p className="text-xl text-discord-text md:pr-12">{feature.description}</p>
                    </div>
                  ))}
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  <Link
                    to="/create"
                    className="discord-btn px-8 py-4 text-lg font-medium flex items-center justify-center gap-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Create a Memorial
                  </Link>
                  <a
                    href="#mission"
                    className="discord-btn-secondary px-8 py-4 text-lg font-medium flex items-center justify-center gap-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                    </svg>
                    Our Mission
                  </a>
                </div>
              </div>
              
              <div className="w-full lg:w-1/2 relative">
                <div className="relative z-10 float-animation">
                  <div className="glass-effect rounded-2xl p-1 shadow-discord-lg overflow-hidden">
                    <div className="bg-discord-dark-bg rounded-xl overflow-hidden p-4">
                      <div className="h-40 bg-gradient-to-r from-discord-blurple to-discord-boost-pink rounded-lg flex items-center justify-center mb-4">
                        <span className="text-3xl font-bold text-white text-shadow-lg">DragonSlayer92</span>
                      </div>
                      <div className="flex items-center mb-4">
                        <div className="w-14 h-14 rounded-full bg-discord-secondary mr-3 flex items-center justify-center text-xl font-bold text-white">
                          D
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-white">DragonSlayer92</h3>
                          <p className="text-discord-muted">Xbox • Gaming Legend</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <p className="text-discord-text">"A legendary guild leader who brought joy to everyone they played with."</p>
                        <div className="flex items-center text-discord-muted text-sm">
                          <span className="flex items-center mr-4">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zM7 8H5v2h2V8zm2 0h2v2H9V8zm6 0h-2v2h2V8z" clipRule="evenodd" />
                            </svg>
                            12 Tributes
                          </span>
                          <span className="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
                            </svg>
                            Permanently Stored
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Decorative background elements */}
                <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-discord-blurple/20 rounded-full blur-3xl"></div>
                <div className="absolute -top-10 -left-10 w-40 h-40 bg-discord-boost-pink/10 rounded-full blur-3xl"></div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Wave divider - improved version */}
        <div className="absolute bottom-0 left-0 right-0 overflow-hidden">
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 1440 320" 
            className="w-full h-auto" 
            preserveAspectRatio="none"
            style={{ display: 'block', verticalAlign: 'middle' }}
          >
            <path 
              fill="var(--discord-bg)" 
              fillOpacity="1" 
              d="M0,96L48,112C96,128,192,160,288,165.3C384,171,480,149,576,138.7C672,128,768,128,864,149.3C960,171,1056,213,1152,208C1248,203,1344,149,1392,122.7L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
            ></path>
          </svg>
        </div>
      </div>

      {/* Mission Section */}
      <div id="mission" className="py-24 px-4 bg-discord-bg relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-discord-blurple/30 to-transparent"></div>
        <div className="absolute -top-40 -left-40 w-80 h-80 bg-discord-blurple/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Our <span className="text-gradient">Mission</span>
            </h2>
            <p className="text-xl text-discord-text mb-8">
              In today's digital world, we form deep, meaningful relationships that exist entirely online. Yet when someone passes away, their online presence eventually fades—usernames go inactive, accounts get deleted, and communities lose a vital connection to someone who mattered.
            </p>
            <p className="text-xl text-discord-text font-medium">
              <span className="text-white">Obituaries.io exists to change this.</span> We believe online connections deserve to be honored, preserved, and remembered with the same dignity as any other relationship.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <h3 className="text-2xl font-bold mb-4 text-white">The Problem We're Solving</h3>
              <ul className="space-y-4">
                {[
                  {
                    title: "Lost Digital Legacies",
                    description: "Over half of deaths aren't memorialized in any formal way, and nearly all digital identities eventually vanish completely.",
                    icon: (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-discord-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )
                  },
                  {
                    title: "Disconnected Communities",
                    description: "Gamers, forum users, social media friends—people who may have spent years together online often have no way to grieve together when someone passes.",
                    icon: (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-discord-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414" />
                      </svg>
                    )
                  },
                  {
                    title: "Unidentifiable Connections",
                    description: "Traditional obituaries use legal names, but online friends may have known someone only by their username for years or even decades.",
                    icon: (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-discord-red" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                      </svg>
                    )
                  },
                ].map((item, index) => (
                  <li key={index} className="flex gap-4 items-start">
                    <div className="mt-1 flex-shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-white">{item.title}</h4>
                      <p className="text-discord-text">{item.description}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="order-1 md:order-2">
              <div className="glass-effect p-6 rounded-xl overflow-hidden shadow-discord-lg">
                <div className="bg-discord-dark-bg rounded-lg p-6">
                  <h3 className="text-2xl font-bold mb-6 text-white text-center">Real Stories</h3>
                  
                  <div className="relative h-64">
                    {stories.map((story, index) => (
                      <div 
                        key={index}
                        className={`absolute inset-0 transition-all duration-700 ease-in-out flex flex-col ${
                          index === activeStory 
                            ? "opacity-100 transform-none" 
                            : "opacity-0 translate-y-8"
                        }`}
                      >
                        <div className="flex flex-col h-full justify-between">
                          <div>
                            <p className="text-discord-text italic mb-4">"{story.quote}"</p>
                            <div className="flex items-center">
                              <div className="w-2 h-10 bg-discord-blurple rounded-full mr-3"></div>
                              <div>
                                <p className="font-semibold text-white">{story.handle}</p>
                                <p className="text-sm text-discord-muted">{story.platform}</p>
                              </div>
                            </div>
                          </div>
                          <div className="text-right text-sm text-discord-muted mt-4">
                            — {story.author}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex justify-center mt-4">
                    {stories.map((_, index) => (
                      <button 
                        key={index}
                        onClick={() => setActiveStory(index)}
                        className={`w-2 h-2 rounded-full mx-1 transition-all ${
                          index === activeStory ? "bg-discord-blurple w-6" : "bg-discord-highlight"
                        }`}
                        aria-label={`Story ${index + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Stats Section */}
      <div id="stats-section" className="py-20 px-4 bg-discord-dark-bg relative overflow-hidden">
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl mx-auto">
            <div className="glass-effect rounded-xl p-8 text-center">
              <h2 className="text-2xl md:text-3xl font-bold mb-12 text-white">The Digital Legacy Crisis</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                <StatCounter 
                  end={63} 
                  label="of people have meaningful online relationships with people they've never met in person" 
                  icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-discord-blurple" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>}
                />
                <StatCounter 
                  end={87} 
                  label="of online identities disappear completely within 5 years of someone's passing" 
                  icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-discord-blurple" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>}
                />
                <StatCounter 
                  end={52} 
                  label="of deaths go unmemorialized in any official capacity online or offline" 
                  icon={<svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-discord-blurple" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>}
                />
              </div>
              
              <div className="mt-12">
                <p className="text-xl text-discord-text">
                  Our digital identities are increasingly becoming our <span className="text-white font-semibold">primary identities</span>. Yet we're losing these important connections and histories every day.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Recent Memorials Section */}
      <div id="recent" className="py-20 px-4 bg-discord-bg">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center mb-12">
            <div>
              <h2 className="text-3xl font-bold mb-2 text-white text-center md:text-left">Recent Memorials</h2>
              <p className="text-discord-text md:max-w-xl">
                Digital legacies being preserved forever, ensuring these identities and their impact won't be forgotten.
              </p>
            </div>
            <Link to="/create" className="discord-btn px-6 py-3 flex items-center gap-2 mt-4 md:mt-0">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              Create Memorial
            </Link>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="discord-spinner"></div>
            </div>
          ) : memorials.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-pop-in">
              {Array.isArray(memorials) && memorials.length > 0 ? memorials.map((memorial) => (
                <Link 
                  to={`/memorial/${memorial.id}`} 
                  key={memorial.id}
                  className="discord-card interactive-hover shine-effect"
                >
                  <div className="h-48 bg-discord-highlight relative">
                    {memorial.banner_image ? (
                      <img 
                        src={memorial.banner_image} 
                        alt="Banner" 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-r from-discord-blurple to-discord-secondary">
                        <span className="text-2xl font-bold text-white text-shadow-md">{memorial.online_handle}</span>
                      </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-xl font-bold text-white text-shadow">{memorial.online_handle}</h3>
                      {memorial.platform && (
                        <p className="text-white/80 text-shadow-sm">{memorial.platform}</p>
                      )}
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-14 h-14 rounded-full bg-discord-blurple shrink-0 overflow-hidden">
                        {memorial.profile_image ? (
                          <img 
                            src={memorial.profile_image} 
                            alt={memorial.online_handle} 
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-r from-discord-blurple to-discord-secondary">
                            <span className="text-xl text-white">{memorial.online_handle ? memorial.online_handle.charAt(0) : '?'}</span>
                          </div>
                        )}
                      </div>
                      <div>
                        {memorial.bio && (
                          <p className="text-discord-text mb-3 line-clamp-2">{memorial.bio}</p>
                        )}
                        <div className="flex items-center text-discord-muted text-sm">
                          <span className="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zM7 8H5v2h2V8zm2 0h2v2H9V8zm6 0h-2v2h2V8z" clipRule="evenodd" />
                            </svg>
                            {memorial.tributes_count} tributes
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              )) : null}
            </div>
          ) : (
            <div className="glass-effect rounded-xl p-8 text-center">
              <div className="w-20 h-20 bg-discord-dark-bg/70 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-discord-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <p className="text-xl text-discord-text mb-6">No memorials have been created yet.</p>
              <Link
                to="/create"
                className="discord-btn px-6 py-3 inline-flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Create the First Memorial
              </Link>
            </div>
          )}
        </div>
      </div>
      
      {/* Technology of Permanence Section */}
      <div id="technology" className="py-24 px-4 bg-discord-bg relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-discord-blurple/30 to-transparent"></div>
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-discord-blurple/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
                The <span className="text-gradient">Technology</span> Behind Eternal Storage
              </h2>
              <p className="text-xl text-discord-text max-w-3xl mx-auto">
                When we say "forever," we mean it. Our technical infrastructure ensures memorials will endure as long as the internet exists.
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <h3 className="text-2xl font-bold mb-6 text-white">How We Achieve Digital Immortality</h3>
                
                <div className="space-y-8">
                  <div className="glass-effect rounded-xl p-6 hover:shadow-discord-glow transition-shadow duration-300">
                    <div className="flex gap-4">
                      <div className="w-12 h-12 bg-discord-dark-bg rounded-xl flex-shrink-0 flex items-center justify-center text-discord-blurple">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-white mb-2">Arweave Blockchain Storage</h4>
                        <p className="text-discord-text">
                          Every memorial and tribute is permanently stored on the Arweave blockchain - a revolutionary "permaweb" technology that uses an endowment model to pay for storage upfront, <span className="text-white">forever</span>. Once data is stored, it can never be deleted or altered.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="glass-effect rounded-xl p-6 hover:shadow-discord-glow transition-shadow duration-300">
                    <div className="flex gap-4">
                      <div className="w-12 h-12 bg-discord-dark-bg rounded-xl flex-shrink-0 flex items-center justify-center text-discord-blurple">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-white mb-2">Redundant CDN Distribution</h4>
                        <p className="text-discord-text">
                          We distribute all memorial data across multiple pre-paid Content Delivery Networks, creating redundant copies that exist across the globe. This ensures quick access and protection against any single point of failure.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="glass-effect rounded-xl p-6 hover:shadow-discord-glow transition-shadow duration-300">
                    <div className="flex gap-4">
                      <div className="w-12 h-12 bg-discord-dark-bg rounded-xl flex-shrink-0 flex items-center justify-center text-discord-blurple">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="text-lg font-semibold text-white mb-2">Permanent Verification Links</h4>
                        <p className="text-discord-text">
                          Each memorial includes immutable verification links to the blockchain storage, allowing anyone to independently verify and access the memorial data, even if our platform somehow disappeared.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="order-1 lg:order-2">
                <div className="glass-effect p-1 rounded-xl overflow-hidden shadow-discord-lg">
                  <div className="bg-discord-dark-bg rounded-xl p-6 md:p-8 relative">
                    {/* Decorative elements */}
                    <div className="absolute top-0 right-0 w-32 h-32 bg-discord-blurple/10 rounded-full blur-xl"></div>
                    <div className="absolute bottom-0 left-0 w-32 h-32 bg-discord-boost-pink/10 rounded-full blur-xl"></div>
                    
                    <div className="relative">
                      <div className="text-center mb-8">
                        <div className="w-20 h-20 mx-auto bg-discord-blurple/20 rounded-full flex items-center justify-center mb-4">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-discord-blurple" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                        </div>
                        <h3 className="text-2xl font-bold text-white mb-2">Eternal by Design</h3>
                        <p className="text-discord-text">
                          Unlike traditional websites that require ongoing payments and maintenance to stay online, Obituaries.io is built on technology that's designed to outlast any single company or platform.
                        </p>
                      </div>
                      
                      <div className="space-y-6">
                        <div className="flex items-center gap-3">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-discord-green" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <p className="text-discord-text"><span className="text-white font-medium">No recurring fees</span> - memorials are preserved forever with a one-time payment</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-discord-green" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <p className="text-discord-text"><span className="text-white font-medium">Decentralized storage</span> - distributed across thousands of nodes worldwide</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-discord-green" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <p className="text-discord-text"><span className="text-white font-medium">Immutable data</span> - once stored, it cannot be deleted or modified</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-discord-green" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <p className="text-discord-text"><span className="text-white font-medium">Self-verifiable</span> - data can be independently accessed and validated</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="py-20 px-4 bg-discord-dark-bg relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-discord-blurple/30 to-transparent"></div>
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-discord-blurple/5 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-discord-boost-pink/5 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-16 text-center text-white">
            How <span className="text-gradient">Obituaries.io</span> Works
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-y-10 md:gap-x-8">
            {[
              {
                step: "1",
                title: "Create a Memorial",
                description: "Add the online identity's handle, platform, and optional information like photos and biography.",
                icon: (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                ),
              },
              {
                step: "2",
                title: "Share With Communities",
                description: "Invite friends, guild members, followers, or other online connections to visit and contribute.",
                icon: (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                ),
              },
              {
                step: "3",
                title: "Preserve Permanently",
                description: "All tributes and memories are stored permanently through Arweave blockchain technology, creating an eternal digital memorial that won't disappear.",
                icon: (
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                ),
              },
            ].map((step, index) => (
              <div key={index} className="glass-effect rounded-xl p-6 relative hover:shadow-discord-glow transition-shadow duration-300">
                <div className="absolute -top-6 left-6 w-12 h-12 rounded-full bg-discord-blurple flex items-center justify-center text-white text-xl font-bold">
                  {step.step}
                </div>
                <div className="pt-6">
                  <div className="w-14 h-14 bg-discord-dark-bg rounded-xl flex items-center justify-center mb-4 text-discord-blurple">
                    {step.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-white">{step.title}</h3>
                  <p className="text-discord-text">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
          
          {/* Call to action */}
          <div className="mt-20 text-center">
            <div className="inline-block p-px rounded-xl bg-gradient-to-r from-discord-blurple via-discord-boost-pink to-discord-green">
              <div className="bg-discord-dark-bg rounded-xl p-8 md:p-12">
                <h3 className="text-2xl md:text-3xl font-bold mb-4 text-white">Preserve a Digital Legacy Today</h3>
                <p className="text-discord-text mb-8 max-w-2xl mx-auto text-lg">
                  Real friendships and meaningful connections don't require physical meetings. Honor the impact someone had on their digital communities by creating a permanent memorial for their online identity.
                </p>
                <Link
                  to="/create"
                  className="discord-btn px-8 py-4 text-lg font-medium inline-flex items-center gap-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Create a Memorial
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Create Memorial Page
const CreateMemorial = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    online_handle: "",
    platform: "",
    real_name: "",
    birth_date: "",
    death_date: "",
    bio: "",
    profile_image: "",
    banner_image: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeStep, setActiveStep] = useState(1);
  const totalSteps = 3;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleImageChange = (e, imageType) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          [imageType]: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/memorials`, formData);
      navigate(`/memorial/${response.data.id}`);
    } catch (err) {
      console.error("Error creating memorial:", err);
      setError("Failed to create memorial. Please try again.");
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (activeStep < totalSteps) {
      setActiveStep(activeStep + 1);
    }
  };

  const prevStep = () => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 1);
    }
  };

  return (
    <div className="min-h-screen bg-discord-darker-bg text-discord-text pt-20 md:pt-28">
      <div className="container mx-auto py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header with steps */}
          <div className="mb-10">
            <h1 className="text-3xl md:text-4xl font-bold mb-6 text-white text-center">Create a Digital Memorial</h1>
            <p className="text-center text-discord-muted mb-8 max-w-2xl mx-auto">
              Honor an online identity by creating a permanent memorial that will be preserved forever.
            </p>
            
            {/* Steps progress */}
            <div className="flex justify-center mb-8">
              <div className="flex items-center w-full max-w-md">
                {[1, 2, 3].map((step) => (
                  <div key={step} className="flex-1 flex flex-col items-center">
                    <div 
                      className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${
                        step === activeStep 
                          ? 'bg-discord-blurple text-white' 
                          : step < activeStep 
                            ? 'bg-discord-green text-white' 
                            : 'bg-discord-light-bg text-discord-muted'
                      }`}
                    >
                      {step < activeStep ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        step
                      )}
                    </div>
                    <span className={`text-sm ${step === activeStep ? 'text-discord-blurple' : 'text-discord-muted'}`}>
                      {step === 1 ? 'Basic Info' : step === 2 ? 'Details' : 'Media'}
                    </span>
                    {step < totalSteps && (
                      <div className={`h-0.5 w-full mt-2 ${step < activeStep ? 'bg-discord-green' : 'bg-discord-light-bg'}`}></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {/* Main content */}
          <div className="glass-effect rounded-xl overflow-hidden">
            <div className="bg-discord-dark-bg p-8">
              {error && (
                <div className="bg-discord-red/20 border border-discord-red text-white p-4 rounded-lg mb-6 animate-pulse">
                  <div className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-discord-red" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {error}
                  </div>
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                {/* Step 1: Basic Info */}
                <div className={activeStep === 1 ? 'block' : 'hidden'}>
                  <div className="mb-6">
                    <label className="block text-discord-text mb-2 font-medium">
                      Online Handle / Username <span className="text-discord-red">*</span>
                    </label>
                    <input
                      type="text"
                      name="online_handle"
                      value={formData.online_handle}
                      onChange={handleChange}
                      required
                      className="discord-input w-full"
                      placeholder="e.g. DragonSlayer92, @ArtLover"
                    />
                    <p className="text-discord-muted text-sm mt-1">
                      The main identifier for this memorial
                    </p>
                  </div>
                  
                  <div className="mb-6">
                    <label className="block text-discord-text mb-2 font-medium">
                      Platform
                    </label>
                    <input
                      type="text"
                      name="platform"
                      value={formData.platform}
                      onChange={handleChange}
                      className="discord-input w-full"
                      placeholder="e.g. Xbox, Twitter, Reddit"
                    />
                    <p className="text-discord-muted text-sm mt-1">
                      Where this handle was primarily used
                    </p>
                  </div>
                  
                  <div className="mb-8">
                    <label className="block text-discord-text mb-2 font-medium">
                      Real Name (optional)
                    </label>
                    <input
                      type="text"
                      name="real_name"
                      value={formData.real_name}
                      onChange={handleChange}
                      className="discord-input w-full"
                      placeholder="Real name (if known)"
                    />
                  </div>
                </div>
                
                {/* Step 2: Details */}
                <div className={activeStep === 2 ? 'block' : 'hidden'}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-discord-text mb-2 font-medium">
                        Birth Date (optional)
                      </label>
                      <input
                        type="date"
                        name="birth_date"
                        value={formData.birth_date}
                        onChange={handleChange}
                        className="discord-input w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-discord-text mb-2 font-medium">
                        Death Date (optional)
                      </label>
                      <input
                        type="date"
                        name="death_date"
                        value={formData.death_date}
                        onChange={handleChange}
                        className="discord-input w-full"
                      />
                    </div>
                  </div>
                  
                  <div className="mb-8">
                    <label className="block text-discord-text mb-2 font-medium">
                      Bio
                    </label>
                    <textarea
                      name="bio"
                      value={formData.bio}
                      onChange={handleChange}
                      rows="6"
                      className="discord-input w-full resize-none"
                      placeholder="Share about this person's online presence, achievements, and what made them special to the community..."
                    />
                    <p className="text-discord-muted text-sm mt-1">
                      This will be prominently displayed on the memorial page
                    </p>
                  </div>
                </div>
                
                {/* Step 3: Media */}
                <div className={activeStep === 3 ? 'block' : 'hidden'}>
                  <div className="grid grid-cols-1 gap-8 mb-8">
                    <div>
                      <label className="block text-discord-text mb-3 font-medium text-lg">
                        Profile Image
                      </label>
                      <div className="bg-discord-bg p-6 rounded-lg">
                        <div className="flex flex-col md:flex-row gap-6 items-center">
                          <div className="w-28 h-28 rounded-full flex items-center justify-center overflow-hidden bg-discord-light-bg border-2 border-discord-highlight/50">
                            {formData.profile_image ? (
                              <img
                                src={formData.profile_image}
                                alt="Profile Preview"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-discord-muted" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                              </svg>
                            )}
                          </div>
                          <div className="flex-1">
                            <p className="text-discord-text mb-3">
                              Upload a profile picture for this memorial. This will be the main image shown in lists and at the top of the memorial page.
                            </p>
                            <label className="discord-btn-secondary px-4 py-2 inline-flex items-center gap-2 cursor-pointer">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                              </svg>
                              Upload Profile Image
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleImageChange(e, "profile_image")}
                                className="hidden"
                              />
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-discord-text mb-3 font-medium text-lg">
                        Banner Image
                      </label>
                      <div className="bg-discord-bg p-6 rounded-lg">
                        <div className="mb-4">
                          <div className="h-40 rounded-lg overflow-hidden bg-discord-light-bg border-2 border-discord-highlight/50">
                            {formData.banner_image ? (
                              <img
                                src={formData.banner_image}
                                alt="Banner Preview"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-discord-muted" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                          </div>
                        </div>
                        <p className="text-discord-text mb-3">
                          Upload a banner image to personalize the memorial page. A good banner might show something related to their online activities or interests.
                        </p>
                        <label className="discord-btn-secondary px-4 py-2 inline-flex items-center gap-2 cursor-pointer">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                          </svg>
                          Upload Banner Image
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleImageChange(e, "banner_image")}
                            className="hidden"
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  <div className="rounded-lg bg-discord-green/10 border border-discord-green/30 p-4 mb-6">
                    <div className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-3 text-discord-green flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      <div>
                        <p className="text-discord-text font-medium mb-1">Permanent Storage</p>
                        <p className="text-discord-muted text-sm">
                          This memorial and all associated tributes will be preserved forever online, ensuring the digital legacy continues to exist in perpetuity.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Navigation buttons */}
                <div className="flex justify-between mt-8">
                  {activeStep > 1 ? (
                    <button
                      type="button"
                      onClick={prevStep}
                      className="discord-btn-secondary px-6 py-3 flex items-center gap-2"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
                      </svg>
                      Back
                    </button>
                  ) : (
                    <div></div> // Empty div to maintain flex layout
                  )}
                  
                  {activeStep < totalSteps ? (
                    <button
                      type="button"
                      onClick={nextStep}
                      className="discord-btn px-6 py-3 flex items-center gap-2"
                    >
                      Next
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={loading}
                      className="discord-btn px-8 py-3 flex items-center gap-2"
                    >
                      {loading ? (
                        <>
                          <div className="w-5 h-5 border-2 border-t-transparent border-white rounded-full animate-spin mr-2"></div>
                          Creating...
                        </>
                      ) : (
                        <>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          Create Memorial
                        </>
                      )}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
          
          {/* Preview panel */}
          <div className="mt-12 text-center">
            <p className="text-discord-muted mb-4">Memorial Preview</p>
            <div className="inline-block glass-effect rounded-lg p-1 shine-effect">
              <div className="bg-discord-dark-bg rounded-lg overflow-hidden">
                <div className="h-32 bg-gradient-to-r from-discord-blurple to-discord-boost-pink flex items-center justify-center relative">
                  {formData.banner_image ? (
                    <img 
                      src={formData.banner_image}
                      alt="Banner Preview"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  ) : null}
                  <div className={`absolute inset-0 flex items-center justify-center ${formData.banner_image ? 'bg-black/30' : ''}`}>
                    <h3 className="text-2xl font-bold text-white text-shadow-lg">
                      {formData.online_handle || "Username"}
                    </h3>
                  </div>
                </div>
                <div className="p-4 flex items-center">
                  <div className="w-12 h-12 rounded-full overflow-hidden mr-3 bg-discord-blurple flex items-center justify-center text-white font-bold">
                    {formData.profile_image ? (
                      <img 
                        src={formData.profile_image}
                        alt="Profile Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      formData.online_handle ? formData.online_handle.charAt(0).toUpperCase() : "U"
                    )}
                  </div>
                  <div>
                    <h4 className="font-medium text-white">
                      {formData.online_handle || "Username"}
                    </h4>
                    <p className="text-sm text-discord-muted">
                      {formData.platform || "Platform"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Memorial Detail Page
const MemorialDetail = () => {
  const { id } = useParams();
  const [memorial, setMemorial] = useState(null);
  const [tributes, setTributes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [tributeForm, setTributeForm] = useState({
    content: "",
    author_name: "",
    image_data: ""
  });

  // Toast notification state
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success"); // success, error

  // Ref for tribute form to scroll to it
  const tributeFormRef = useRef(null);

  useEffect(() => {
    const fetchMemorial = async () => {
      try {
        const [memorialRes, tributesRes] = await Promise.all([
          axios.get(`${API}/memorials/${id}`),
          axios.get(`${API}/tributes/memorial/${id}`)
        ]);
        
        setMemorial(memorialRes.data);
        setTributes(tributesRes.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching memorial:", err);
        setError("Failed to load memorial");
        setLoading(false);
      }
    };

    fetchMemorial();

    // Scroll to top when page loads
    window.scrollTo(0, 0);
  }, [id]);

  const handleTributeChange = (e) => {
    const { name, value } = e.target;
    setTributeForm({
      ...tributeForm,
      [name]: value
    });
  };

  const handleTributeImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setTributeForm({
          ...tributeForm,
          image_data: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const scrollToTributeForm = () => {
    if (tributeFormRef.current) {
      tributeFormRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      
      // Focus the textarea
      setTimeout(() => {
        const textarea = tributeFormRef.current.querySelector('textarea');
        if (textarea) {
          textarea.focus();
        }
      }, 600);
    }
  };

  const showToastMessage = (message, type = "success") => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    
    // Auto-hide after 4 seconds
    setTimeout(() => {
      setShowToast(false);
    }, 4000);
  };

  const submitTribute = async (e) => {
    e.preventDefault();
    
    if (!tributeForm.content.trim()) {
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/tributes`, {
        ...tributeForm,
        memorial_id: id
      });
      
      setTributes([response.data, ...tributes]);
      
      // Reset form
      setTributeForm({
        content: "",
        author_name: "",
        image_data: ""
      });
      
      showToastMessage("Your tribute has been added to the memorial");
    } catch (err) {
      console.error("Error posting tribute:", err);
      showToastMessage("Failed to post your tribute. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-discord-darker-bg text-white flex items-center justify-center">
        <div className="discord-spinner"></div>
      </div>
    );
  }

  if (error || !memorial) {
    return (
      <div className="min-h-screen bg-discord-darker-bg text-white flex flex-col items-center justify-center p-4">
        <div className="glass-effect rounded-xl p-8 max-w-lg w-full text-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-discord-red mx-auto mb-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="text-2xl font-bold mb-4">Error</h2>
          <p className="text-lg mb-6 text-discord-text">{error || "Memorial not found"}</p>
          <Link to="/" className="discord-btn px-6 py-3 inline-flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return "";
    
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <div className="min-h-screen bg-discord-darker-bg text-discord-text pt-16 md:pt-16">
      {/* Toast notification */}
      <div className={`fixed top-24 right-4 z-50 transition-all duration-500 transform ${showToast ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}`}>
        <div className={`rounded-lg shadow-lg p-4 flex items-center ${toastType === 'success' ? 'bg-discord-green/90' : 'bg-discord-red/90'} backdrop-blur-sm text-white max-w-xs`}>
          {toastType === 'success' ? (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          )}
          <p>{toastMessage}</p>
        </div>
      </div>
      
      {/* Banner Image */}
      <div className="h-80 md:h-[500px] bg-discord-dark-bg relative overflow-hidden">
        {memorial.banner_image ? (
          <div className="absolute inset-0">
            <img 
              src={memorial.banner_image} 
              alt="Memorial Banner" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-discord-darker-bg/30 to-discord-darker-bg"></div>
          </div>
        ) : (
          <div className="absolute inset-0 animated-gradient opacity-30"></div>
        )}
        
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <h1 className="text-5xl md:text-7xl font-bold text-white text-shadow-lg mb-4 tracking-tight">{memorial.online_handle}</h1>
          {memorial.platform && (
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-discord-blurple/80 backdrop-blur-sm text-white text-lg font-medium mb-6">
              {memorial.platform}
            </div>
          )}
          
          <button
            onClick={scrollToTributeForm}
            className="discord-btn-secondary mt-8 px-6 py-3 flex items-center gap-2 backdrop-blur-sm z-10 animate-bounce"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zM7 8H5v2h2V8zm2 0h2v2H9V8zm6 0h-2v2h2V8z" clipRule="evenodd" />
            </svg>
            Share a Memory
          </button>
        </div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10 -mt-20">
        <div className="glass-effect rounded-xl overflow-hidden shadow-discord-lg">
          <div className="bg-discord-dark-bg/95 backdrop-blur-sm">
            {/* Memorial Header */}
            <div className="p-6 md:p-10">
              <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-discord-blurple/70 shadow-discord-glow">
                  {memorial.profile_image ? (
                    <img 
                      src={memorial.profile_image} 
                      alt={memorial.online_handle}
                      className="w-full h-full object-cover" 
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-r from-discord-blurple to-discord-boost-pink">
                      <span className="text-4xl font-bold text-white">{memorial.online_handle.charAt(0)}</span>
                    </div>
                  )}
                </div>
                
                <div className="text-center md:text-left flex-1">
                  <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
                    <h1 className="text-3xl md:text-4xl font-bold text-white">{memorial.online_handle}</h1>
                    
                    {/* Permanent badge */}
                    <div className="inline-flex items-center gap-1 bg-discord-green/20 border border-discord-green/30 px-3 py-1 rounded-full">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-discord-green" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                      </svg>
                      <span className="text-discord-green text-sm font-medium">Permanent</span>
                    </div>
                  </div>
                  
                  {memorial.real_name && (
                    <p className="text-xl text-discord-text mb-3">Real Name: {memorial.real_name}</p>
                  )}
                  
                  <div className="flex flex-wrap justify-center md:justify-start gap-x-8 mb-4 text-discord-muted">
                    {memorial.birth_date && (
                      <div className="flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M6 3a1 1 0 011-1h.01a1 1 0 010 2H7a1 1 0 01-1-1zm2 3a1 1 0 00-2 0v1a2 2 0 00-2 2v1a2 2 0 00-2 2v.683a3.7 3.7 0 011.055.485 1.704 1.704 0 001.89 0 3.704 3.704 0 014.11 0 1.704 1.704 0 001.89 0 3.704 3.704 0 014.11 0 1.704 1.704 0 001.89 0A3.7 3.7 0 0118 12.683V12a2 2 0 00-2-2V9a2 2 0 00-2-2V6a1 1 0 10-2 0v1h-1V6a1 1 0 10-2 0v1H8V6zm10 8.868a3.704 3.704 0 01-4.055-.036 1.704 1.704 0 00-1.89 0 3.704 3.704 0 01-4.11 0 1.704 1.704 0 00-1.89 0A3.704 3.704 0 012 14.868V17a1 1 0 001 1h14a1 1 0 001-1v-2.132zM9 3a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1zm3 0a1 1 0 011-1h.01a1 1 0 110 2H13a1 1 0 01-1-1z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <span className="text-sm block">Born</span>
                          <span>{formatDate(memorial.birth_date)}</span>
                        </div>
                      </div>
                    )}
                    
                    {memorial.death_date && (
                      <div className="flex items-center gap-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM7 9a1 1 0 000 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                        </svg>
                        <div>
                          <span className="text-sm block">Died</span>
                          <span>{formatDate(memorial.death_date)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex flex-wrap justify-center md:justify-start gap-3">
                    <button 
                      onClick={scrollToTributeForm} 
                      className="discord-btn px-4 py-2 flex items-center gap-2"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zM7 8H5v2h2V8zm2 0h2v2H9V8zm6 0h-2v2h2V8z" clipRule="evenodd" />
                      </svg>
                      Share a Memory
                    </button>
                    
                    <Link to="/" className="discord-btn-secondary px-4 py-2 flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                      </svg>
                      Home
                    </Link>
                  </div>
                </div>
              </div>
              
              {memorial.bio && (
                <div className="mt-8 border-t border-discord-highlight/30 pt-6">
                  <h2 className="text-xl font-semibold mb-3 text-white">About {memorial.online_handle}</h2>
                  <p className="text-discord-text whitespace-pre-line">{memorial.bio}</p>
                </div>
              )}
            </div>
            
            {/* Permanent Storage Notice */}
            <div className="bg-gradient-to-r from-discord-blurple/10 to-discord-boost-pink/10 p-4 border-t border-b border-discord-highlight/20 pulse-animation">
              <div className="flex items-center justify-center gap-3">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-discord-blurple" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
                <p className="text-center text-discord-text">
                  This memorial is designed to be preserved forever online. All memories and tributes will remain permanently archived.
                </p>
              </div>
            </div>
            
            {/* Tributes Section */}
            <div className="p-6 md:p-10">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-bold text-white">Tributes & Memories</h2>
                <div className="text-discord-muted flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
                    <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
                  </svg>
                  {tributes.length} {tributes.length === 1 ? 'tribute' : 'tributes'}
                </div>
              </div>
              
              {/* Add Tribute Form */}
              <div ref={tributeFormRef} className="mb-10 glass-effect rounded-xl overflow-hidden">
                <div className="bg-discord-dark-bg/60 p-6">
                  <form onSubmit={submitTribute}>
                    <h3 className="text-xl font-semibold mb-4 text-white flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-discord-blurple" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zM7 8H5v2h2V8zm2 0h2v2H9V8zm6 0h-2v2h2V8z" clipRule="evenodd" />
                      </svg>
                      Share a Memory
                    </h3>
                    
                    <div className="mb-4">
                      <textarea
                        name="content"
                        value={tributeForm.content}
                        onChange={handleTributeChange}
                        rows="4"
                        required
                        className="discord-input w-full resize-none"
                        placeholder="Share a memory, story, or message about this person..."
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div>
                        <label className="block text-discord-text mb-2 text-sm font-medium">
                          Your Name (optional)
                        </label>
                        <input
                          type="text"
                          name="author_name"
                          value={tributeForm.author_name}
                          onChange={handleTributeChange}
                          className="discord-input w-full"
                          placeholder="Anonymous"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-discord-text mb-2 text-sm font-medium">
                          Add an Image (optional)
                        </label>
                        <label className="discord-btn-secondary px-4 py-2 inline-flex items-center gap-2 cursor-pointer">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                          </svg>
                          Upload Image
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleTributeImageChange}
                            className="hidden"
                          />
                        </label>
                      </div>
                    </div>
                    
                    {tributeForm.image_data && (
                      <div className="mb-4 p-2 border border-discord-highlight/30 rounded-lg bg-discord-bg/50">
                        <div className="relative">
                          <img
                            src={tributeForm.image_data}
                            alt="Preview"
                            className="h-32 object-contain rounded"
                          />
                          <button
                            type="button"
                            onClick={() => setTributeForm({ ...tributeForm, image_data: "" })}
                            className="absolute top-2 right-2 bg-discord-red/90 rounded-full p-1 hover:bg-discord-red transition-colors"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex justify-between items-center">
                      <p className="text-discord-muted text-sm">
                        Your tribute will be permanently stored
                      </p>
                      <button
                        type="submit"
                        disabled={submitting}
                        className="discord-btn px-6 py-2 flex items-center gap-2"
                      >
                        {submitting ? (
                          <>
                            <div className="w-5 h-5 border-2 border-t-transparent border-white rounded-full animate-spin"></div>
                            Posting...
                          </>
                        ) : (
                          <>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
                            </svg>
                            Post Tribute
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
              
              {/* Tributes List */}
              {tributes.length > 0 ? (
                <div className="space-y-6 stagger-pop-in">
                  {tributes.map((tribute) => (
                    <div key={tribute.id} className="glass-effect rounded-xl overflow-hidden interactive-hover">
                      <div className="bg-discord-dark-bg/60 p-6">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex items-center">
                            <div className="w-10 h-10 rounded-full bg-discord-light-bg flex items-center justify-center text-discord-text mr-3">
                              {tribute.author_name ? (
                                <span className="font-bold">{tribute.author_name[0]}</span>
                              ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                            <div>
                              <h4 className="font-semibold text-white">
                                {tribute.author_name || "Anonymous"}
                              </h4>
                              <p className="text-xs text-discord-muted">
                                {new Date(tribute.created_at).toLocaleDateString(undefined, {
                                  year: 'numeric',
                                  month: 'short',
                                  day: 'numeric'
                                })}
                              </p>
                            </div>
                          </div>
                          
                          <div className="text-discord-muted">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
                            </svg>
                          </div>
                        </div>
                        
                        <p className="text-discord-text mb-4 whitespace-pre-line">{tribute.content}</p>
                        
                        {tribute.image_data && (
                          <div className="mt-3 border border-discord-highlight/10 rounded-lg overflow-hidden bg-discord-bg/30">
                            <img
                              src={tribute.image_data}
                              alt="Tribute Image"
                              className="max-h-96 w-full object-contain"
                            />
                          </div>
                        )}
                        
                        <div className="mt-4 pt-2 border-t border-discord-highlight/20 flex justify-between items-center">
                          <div className="flex gap-4 text-discord-muted">
                            <button className="flex items-center gap-1 hover:text-discord-text transition-colors">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                              </svg>
                              <span className="text-sm">Like</span>
                            </button>
                            <button className="flex items-center gap-1 hover:text-discord-text transition-colors" onClick={scrollToTributeForm}>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zM7 8H5v2h2V8zm2 0h2v2H9V8zm6 0h-2v2h2V8z" clipRule="evenodd" />
                              </svg>
                              <span className="text-sm">Reply</span>
                            </button>
                          </div>
                          
                          <div className="text-discord-muted text-xs flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z" />
                            </svg>
                            Permanently Stored
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="glass-effect rounded-xl p-10 text-center">
                  <div className="w-20 h-20 bg-discord-blurple/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-discord-blurple" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 5v8a2 2 0 01-2 2h-5l-5 4v-4H4a2 2 0 01-2-2V5a2 2 0 012-2h12a2 2 0 012 2zM7 8H5v2h2V8zm2 0h2v2H9V8zm6 0h-2v2h2V8z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-xl text-discord-text mb-4">No tributes yet</p>
                  <p className="text-discord-muted mb-6">Be the first to share a memory about {memorial.online_handle}</p>
                  <button
                    onClick={scrollToTributeForm}
                    className="discord-btn px-6 py-3 inline-flex items-center gap-2"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
                    </svg>
                    Add First Tribute
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Footer info */}
        <div className="text-center py-10 text-discord-muted">
          <p className="mb-2">This memorial will be permanently preserved online</p>
          <div className="flex justify-center items-center gap-2">
            <span>Share this memorial:</span>
            <button className="text-discord-blurple hover:text-discord-secondary transition-colors">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  // Add a loading state for initial page transition
  const [pageLoading, setPageLoading] = useState(true);

  useEffect(() => {
    // Simulate loading for smooth initial transition
    const timer = setTimeout(() => {
      setPageLoading(false);
    }, 800);
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className={`App text-discord-text bg-discord-darker-bg min-h-screen flex flex-col`}>
      {pageLoading ? (
        <div className="fixed inset-0 bg-discord-darker-bg z-50 flex items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 border-4 border-discord-blurple border-t-transparent rounded-full animate-spin mb-4"></div>
            <div className="text-discord-blurple text-lg font-medium">Loading...</div>
          </div>
        </div>
      ) : (
        <BrowserRouter>
          <Header />
          <main className="flex-grow pt-16">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/create" element={<CreateMemorial />} />
              <Route path="/memorial/:id" element={<MemorialDetail />} />
            </Routes>
          </main>
          <Footer />
        </BrowserRouter>
      )}
    </div>
  );
}

export default App;
